import React, { useState, useEffect } from 'react';
import { Form } from 'react-bootstrap';
import axios from 'axios';
import Swal from 'sweetalert2';
import { apiHandler } from '../../api/apihandler';
import { QUICKBOOK_CARD_LIST, QUICKBOOK_CARD_SAVE, BILLING_PAY_QUICKBOOKS } from '../../api/constants';
import LoaderModal from '../loaderModal';

// Phase 16 - reuses the EXISTING, working QuickBooks mechanism from
// my-transactions.js (the tuition-rail payment screen) rather than building
// a second QuickBooks implementation. Card tokenization goes straight to
// Intuit's own hosted endpoint (api.intuit.com/quickbooks/v4/payments/tokens)
// - the raw card number/CVV never reaches HarmonyDayCare's own backend,
// only the opaque token Intuit returns does. QUICKBOOK_CARD_LIST/
// QUICKBOOK_CARD_SAVE (existing endpoints) manage the saved-card list; the
// only new call is the final payment, which goes to the Phase 12
// BILLING_PAY_QUICKBOOKS endpoint (built in Phase 12 to accept exactly the
// same {user_quickbook_card_id, token} shape GRADE_TRANSACTION_PAY already
// uses) instead of GRADE_TRANSACTION_PAY - so it reaches the billing
// engine's PaymentRecordingService, never the tuition rail.
function getNormalizedQuickbookCards(res) {
  const source = res?.data?.data?.user_quickbook_cards || res?.data?.data?.cards || res?.data?.data?.data || [];
  const list = Array.isArray(source) ? source : (Array.isArray(source?.data) ? source.data : []);
  return list
    .filter((card) => Number(card?.status ?? 1) === 1)
    .map((card) => ({
      id: card?.id || card?.user_quickbook_card_id || card?.userQuickbookCardId,
      quickbookCardId: card?.quickbook_card_id || card?.quickbookCardId,
      name: card?.name,
      last4: card?.last4 || card?.card_last4,
      brand: card?.brand || card?.card_brand,
    }))
    .filter((card) => card?.id);
}

function formatCardInput(value) {
  const raw = value.replace(/\D/g, '').slice(0, 16);
  return raw.replace(/(.{4})/g, '$1 ').trim();
}

function QuickbooksCardForBilling({ amount, payFullBalance = false, onSuccess, onCancel }) {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [cards, setCards] = useState([]);
  const [selectedCardId, setSelectedCardId] = useState('');
  const [view, setView] = useState('pay'); // 'pay' | 'add-card'

  const [cardHolder, setCardHolder] = useState('');
  const [cardNumber, setCardNumber] = useState('');
  const [expMonth, setExpMonth] = useState('01');
  const [expYear, setExpYear] = useState(String(new Date().getFullYear()));
  const [cvv, setCvv] = useState('');
  const [zip, setZip] = useState('');

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await apiHandler(QUICKBOOK_CARD_LIST, 'POST', {});
        const loaded = res?.data?.response?.status === 200 ? getNormalizedQuickbookCards(res) : [];
        setCards(loaded);
        setSelectedCardId(String(loaded[0]?.id || ''));
        setView(loaded.length ? 'pay' : 'add-card');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const addCardAndContinue = async () => {
    if (!cardHolder.trim()) {
      Swal.fire({ title: 'Required', text: 'Please enter card holder name.', icon: 'warning', confirmButtonText: 'OK' });
      return;
    }
    const cleanNumber = cardNumber.replace(/\s/g, '');
    if (cleanNumber.length < 12) {
      Swal.fire({ title: 'Required', text: 'Please enter a valid card number.', icon: 'warning', confirmButtonText: 'OK' });
      return;
    }
    if (!cvv.trim() || cvv.trim().length < 3) {
      Swal.fire({ title: 'Required', text: 'Please enter a valid CVV.', icon: 'warning', confirmButtonText: 'OK' });
      return;
    }

    setSubmitting(true);
    try {
      const tokenApiUrl = process.env.REACT_APP_QUICKBOOK_TOKEN_URL || 'https://api.intuit.com/quickbooks/v4/payments/tokens';
      const tokenResponse = await axios.post(
        tokenApiUrl,
        {
          card: {
            name: cardHolder.trim(),
            number: cleanNumber,
            expMonth,
            expYear,
            cvc: cvv.trim(),
            address: { postalCode: zip, country: 'US' },
          },
        },
        { headers: { 'Content-Type': 'application/json' } }
      );

      const quickbookToken = tokenResponse?.data?.value;
      if (!quickbookToken) {
        Swal.fire({ title: 'Error', text: 'Unable to tokenize card with QuickBooks. Please try again.', icon: 'error', confirmButtonText: 'OK' });
        return;
      }

      const saveRes = await apiHandler(QUICKBOOK_CARD_SAVE, 'POST', { token: quickbookToken, name: cardHolder.trim() || undefined });
      if (saveRes?.data?.response?.status !== 200) {
        Swal.fire({ title: 'Error', text: saveRes?.data?.response?.message || 'Unable to save card.', icon: 'error', confirmButtonText: 'OK' });
        return;
      }

      const listRes = await apiHandler(QUICKBOOK_CARD_LIST, 'POST', {});
      const refreshed = listRes?.data?.response?.status === 200 ? getNormalizedQuickbookCards(listRes) : [];
      setCards(refreshed);
      setSelectedCardId(String(refreshed[0]?.id || ''));
      setView('pay');
    } catch (error) {
      const msg = error?.response?.data?.errors?.[0]?.message || 'Something went wrong while tokenizing card.';
      Swal.fire({ title: 'Error', text: msg, icon: 'error', confirmButtonText: 'OK' });
    } finally {
      setSubmitting(false);
    }
  };

  const payWithSelectedCard = async () => {
    if (submitting) {
      return;
    }
    if (!selectedCardId) {
      Swal.fire({ title: 'Card Required', text: 'Please select a saved card to continue.', icon: 'warning', confirmButtonText: 'OK' });
      return;
    }
    setSubmitting(true);
    try {
      const res = await apiHandler(BILLING_PAY_QUICKBOOKS, 'POST', {
        amount: Number(amount).toFixed(2),
        // Server charges exactly the outstanding balance when set.
        ...(payFullBalance ? { pay_full_balance: true } : {}),
        user_quickbook_card_id: selectedCardId,
      });

      if (res?.isAxiosError || (res?.response && res?.data === undefined)) {
        Swal.fire({ title: 'Payment Failed', text: res?.response?.data?.data?.message || res?.response?.data?.message || 'Unable to process payment. Please try again.', icon: 'error', confirmButtonText: 'OK' });
        return;
      }

      if (res.data?.response?.status === 200 || res.data?.response?.status === 201) {
        // pending_recording: charged but not yet recorded - show the server's
        // "do not pay again" message. Otherwise report what the server
        // recorded (pay_full_balance charges its own figure).
        const data = res.data?.data;
        Swal.fire({
          title: data?.pending_recording ? 'Payment Received' : 'Payment Successful',
          text: data?.pending_recording
            ? data.message
            : `Your payment of $${Number(data?.payment?.amount ?? amount).toFixed(2)} was received.`,
          icon: 'success',
          confirmButtonText: 'Okay',
        });
        // Never assumed successful until the backend itself confirmed it.
        onSuccess && onSuccess();
      } else {
        Swal.fire({ title: 'Payment Failed', text: res?.data?.data?.message || 'Unable to process payment. Please try again.', icon: 'error', confirmButtonText: 'OK' });
      }
    } catch (error) {
      Swal.fire({ title: 'Payment Failed', text: 'Something went wrong while processing your payment. Please try again.', icon: 'error', confirmButtonText: 'OK' });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="text-center py-3"><span className="spinner-border spinner-border-sm" /></div>;
  }

  return (
    <>
      <LoaderModal showLoader={submitting} loaderData="Processing your payment..." />
      {view === 'pay' ? (
        <>
          <Form.Group className="mb-3">
            <Form.Label>Saved Card</Form.Label>
            <Form.Select value={selectedCardId} onChange={(e) => setSelectedCardId(e.target.value)}>
              {cards.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.brand || 'Card'} {c.last4 ? `•••• ${c.last4}` : ''} {c.name ? `(${c.name})` : ''}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          <div className="d-flex justify-content-between mb-3">
            <button type="button" className="btn btn-link p-0" onClick={() => setView('add-card')} disabled={submitting}>
              + Use a different card
            </button>
          </div>
          <div className="d-flex justify-content-end gap-2">
            <button type="button" className="btn btn-outline-secondary" disabled={submitting} onClick={onCancel}>Cancel</button>
            <button type="button" className="btn btn-primary" disabled={submitting} onClick={payWithSelectedCard}>
              {submitting ? 'Processing...' : `Pay $${Number(amount || 0).toFixed(2)}`}
            </button>
          </div>
        </>
      ) : (
        <>
          <Form.Group className="mb-2">
            <Form.Label>Card Holder Name</Form.Label>
            <Form.Control value={cardHolder} onChange={(e) => setCardHolder(e.target.value)} />
          </Form.Group>
          <Form.Group className="mb-2">
            <Form.Label>Card Number</Form.Label>
            <Form.Control value={cardNumber} onChange={(e) => setCardNumber(formatCardInput(e.target.value))} placeholder="0000 0000 0000 0000" />
          </Form.Group>
          <div className="d-flex gap-2 mb-2">
            <Form.Group className="flex-fill">
              <Form.Label>Exp Month</Form.Label>
              <Form.Select value={expMonth} onChange={(e) => setExpMonth(e.target.value)}>
                {Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, '0')).map((m) => <option key={m} value={m}>{m}</option>)}
              </Form.Select>
            </Form.Group>
            <Form.Group className="flex-fill">
              <Form.Label>Exp Year</Form.Label>
              <Form.Select value={expYear} onChange={(e) => setExpYear(e.target.value)}>
                {Array.from({ length: 15 }, (_, i) => String(new Date().getFullYear() + i)).map((y) => <option key={y} value={y}>{y}</option>)}
              </Form.Select>
            </Form.Group>
            <Form.Group className="flex-fill">
              <Form.Label>CVV</Form.Label>
              <Form.Control value={cvv} onChange={(e) => setCvv(e.target.value.replace(/\D/g, '').slice(0, 4))} />
            </Form.Group>
          </div>
          <Form.Group className="mb-3">
            <Form.Label>ZIP / Postal Code</Form.Label>
            <Form.Control value={zip} onChange={(e) => setZip(e.target.value)} />
          </Form.Group>
          <div className="d-flex justify-content-end gap-2">
            {cards.length > 0 && (
              <button type="button" className="btn btn-outline-secondary" disabled={submitting} onClick={() => setView('pay')}>Back</button>
            )}
            <button type="button" className="btn btn-primary" disabled={submitting} onClick={addCardAndContinue}>
              {submitting ? 'Saving...' : 'Save Card & Continue'}
            </button>
          </div>
        </>
      )}
    </>
  );
}

export default QuickbooksCardForBilling;
